// ./src/components/features/Inventory/inventoryHelpers.ts
//
// Display + grouping helpers for inventory stock. Balances come back as flat lot
// rows (one per location + expiry lot); the event view groups them by item, sums
// on-hand + value, and flags the soonest expiry (expiring-soon / expired). Keeps the
// lot/expiry semantics in one place so the balances list and ops console agree.

import dayjs from "dayjs";
import type { TFunction } from "i18next";

import { formatCurrency } from "@/utils";
import { InventoryBalance, InventoryItem, InventoryTxn, INVENTORY_TXN_OP, INVENTORY_ADJUST_REASON_LABELS } from "@/utils/api";

import { expiryState, formatExpiry, type ExpiryState } from "./inventoryExpiry";

// Translate with an English fallback so these display helpers localize when a `t` is threaded in
// (the callers pass their `useTranslation` t) and stay readable when one isn't.
const tr = (t: TFunction | undefined, key: string, dflt: string): string => (t ? t(key, { defaultValue: dflt }) : dflt);

// The expiry rules live in a leaf (`inventoryExpiry.ts`, run by `check-event-zone`); re-exported for the callers.
export { EXPIRING_SOON_DAYS, expiryState, formatExpiry } from "./inventoryExpiry";
export type { ExpiryState } from "./inventoryExpiry";

/** A location label for a lot ("This event", a venue name, or "Home"). */
export const balanceLocationLabel = (b: InventoryBalance, thisEventId?: string | null, t?: TFunction): string => {
  if (b.eventId) {
    return b.eventId === thisEventId
      ? tr(t, "inventory:location.thisEvent", "This event")
      : b.event?.name || tr(t, "inventory:location.event", "Event");
  }

  if (b.venue?.name || b.venueId) {
    return b.venue?.name || tr(t, "inventory:location.homeVenue", "Home venue");
  }

  return tr(t, "inventory:location.home", "Home");
};

/** One item's rolled-up stock across its lots at the queried scope. */
export interface GroupedBalance {
  inventoryItemId: string;
  item?: InventoryItem | null;
  onHand: number;
  value: number;
  lots: InventoryBalance[];
  soonestExpiry: string | null;
  worstExpiryState: ExpiryState;
  /** Item-level low-stock alert (authoritative, from the balance rows — total on-hand vs threshold). */
  lowStock: boolean;
}

const EXPIRY_SEVERITY: Record<ExpiryState, number> = { expired: 3, soon: 2, ok: 1, none: 0 };

/** Group flat lot rows by item, summing on-hand + value and finding the soonest expiry. */
export const groupBalancesByItem = (
  balances: InventoryBalance[],
  itemsById: Map<string, InventoryItem>
): GroupedBalance[] => {
  const groups = new Map<string, GroupedBalance>();

  for (const b of balances) {
    const id = b.inventoryItemId;

    if (!id) {
      continue;
    }

    let g = groups.get(id);

    if (!g) {
      g = {
        inventoryItemId: id,
        // Prefer the name resolved live on the balance row (works for items not on
        // the loaded catalog page); fall back to the catalog lookup.
        item: b.item ?? itemsById.get(id) ?? null,
        onHand: 0,
        value: 0,
        lots: [],
        soonestExpiry: null,
        worstExpiryState: "none",
        lowStock: false,
      };
      groups.set(id, g);
    }

    g.onHand += Number(b.onHand) || 0;
    g.value += Number(b.value) || 0;
    g.lots.push(b);
    if (b.lowStock) {
      g.lowStock = true;
    }

    if (b.expiresAt) {
      if (!g.soonestExpiry || dayjs(b.expiresAt).isBefore(dayjs(g.soonestExpiry))) {
        g.soonestExpiry = b.expiresAt;
      }
    }

    const state = expiryState(b.expiresAt);

    if (EXPIRY_SEVERITY[state] > EXPIRY_SEVERITY[g.worstExpiryState]) {
      g.worstExpiryState = state;
    }
  }

  return Array.from(groups.values()).sort((a, b) =>
    (a.item?.name || "").localeCompare(b.item?.name || "")
  );
};

/** A quantity + unit rendered together, e.g. "12 each". */
export const formatQtyWithUnit = (qty?: number | null, unit?: string | null): string => {
  const n = Number(qty) || 0;
  const u = (unit || "").trim();

  return u ? `${n} ${u}` : String(n);
};

/** The item's stock-unit label (a can, a chair…), defaulting to "each". */
export const inventoryUnitLabel = (item?: { unit?: string | null } | null): string => (item?.unit || "each").trim() || "each";

/** The largest pack size among an item's barcodes (a 32-can box → 32); 1 when none pack. */
export const largestPackSize = (item?: { barcodes?: { unitsPerCode?: number | null }[] | null } | null): number => {
  const sizes = (item?.barcodes ?? []).map((b) => Number(b?.unitsPerCode) || 1).filter((n) => n > 1);

  return sizes.length ? Math.max(...sizes) : 1;
};

/**
 * A buy-list pack hint: how many of the largest pack cover a base-unit quantity —
 * "≈ 6 × 32-pack". Null when the item has no pack barcodes (buy in units) or the
 * quantity is empty. The order is still expressed in the base unit; this is guidance.
 */
export const packHintText = (item: { barcodes?: { unitsPerCode?: number | null }[] | null } | null | undefined, quantity?: number | null): string | null => {
  const pack = largestPackSize(item);
  const qty = Number(quantity) || 0;

  if (pack <= 1 || qty <= 0) {
    return null;
  }

  return `≈ ${Math.ceil(qty / pack)} × ${pack}-pack`;
};

/** A money amount in its currency, e.g. "$16.00". Falls back gracefully on an odd code. */
// Delegates to the app-wide `formatCurrency` (@em/utils) so inventory money renders identically
// to the rest of the app (Budget/Rentals) — one source of truth, no format divergence.
export const formatMoney = (amount?: number | null, currency?: string | null): string => formatCurrency(Number(amount) || 0, currency || undefined);

/** A rich, human summary of a completed operation, built from the returned ledger rows. */
export interface OperationSummary {
  /** The item name (prominent line). */
  title: string;
  /** The headline quantity, e.g. "64 cans" / "Counted 64 (+14)". */
  headline: string;
  /** Total value moved, e.g. "$32.00" — null when no cost was recorded. */
  amount: string | null;
  /** Detail lines — quantity, pack math, price, location, lot. */
  lines: string[];
  /** One-line form for the session "recent" feed. */
  compact: string;
}

/**
 * Turn the ledger rows a completed operation returned into a readable summary
 * (item, total quantity + unit, pack math, price + total value, location(s), lot)
 * for the console success banner and the recent feed — so "Receive recorded /
 * Sprite" becomes "Sprite · 64 cans (2 × pack of 32) · $0.50/can · $32.00 total ·
 * Home". Numbers come straight off the rows (already unit-based, cost per unit).
 */
export const describeOperation = (
  opLabel: string,
  rows: InventoryTxn[],
  item?: Pick<InventoryItem, "name" | "unit"> | null,
  opts: { thisEventId?: string | null; t?: TFunction } = {},
): OperationSummary => {
  const first = rows[0] || ({} as InventoryTxn);
  const op = first.op || "";
  const unit = item?.unit;
  const t = opts.t;
  const name = item?.name || tr(t, "inventory:item.fallbackName", "Item");
  const lines: string[] = [];

  // Total units this op moved (op-specific), and the headline quantity text.
  let totalUnits = 0;
  let qtyText = "";

  if (op === INVENTORY_TXN_OP.COUNT) {
    const counted = Number(first.countedQty) || 0;
    const delta = Number(first.qtyDelta) || 0;
    totalUnits = counted;
    qtyText =
      `${tr(t, "inventory:op.counted", "Counted")} ${formatQtyWithUnit(counted, unit)}` +
      (delta === 0 ? ` (${tr(t, "inventory:op.noChange", "no change")})` : ` (${delta > 0 ? "+" : ""}${delta})`);
  } else if (op === INVENTORY_TXN_OP.ADJUST) {
    const delta = Number(first.qtyDelta) || 0;
    totalUnits = Math.abs(delta);
    // A `counted_qty` on an adjust row means it came from a count that didn't match
    // the system — show what was counted before the categorized correction.
    const fromCount = first.countedQty != null;
    qtyText =
      (fromCount ? `${tr(t, "inventory:op.counted", "Counted")} ${formatQtyWithUnit(Number(first.countedQty) || 0, unit)} · ` : "") +
      `${delta > 0 ? "+" : ""}${formatQtyWithUnit(delta, unit)}`;
    const reason = first.reason ? INVENTORY_ADJUST_REASON_LABELS[first.reason] || first.reason : null;
    if (reason) {
      qtyText += ` · ${reason}`;
    }
  } else if (op === INVENTORY_TXN_OP.TRANSFER || op === INVENTORY_TXN_OP.RETURN) {
    totalUnits = rows.filter((r) => Number(r.qtyDelta) > 0).reduce((s, r) => s + (Number(r.qtyDelta) || 0), 0);
    qtyText = formatQtyWithUnit(totalUnits, unit);
  } else {
    // receive, consume
    totalUnits = rows.reduce((s, r) => s + Math.abs(Number(r.qtyDelta) || 0), 0);
    qtyText = formatQtyWithUnit(totalUnits, unit);
  }

  lines.push(qtyText);

  // Pack math (a pack size was applied on entry).
  const packUnits = Number(first.packUnits) || 0;
  if (packUnits > 1 && totalUnits > 0) {
    const packs = Math.round((totalUnits / packUnits) * 100) / 100;
    lines.push(`${packs} × ${tr(t, "inventory:op.packOf", "pack of")} ${packUnits}`);
  }

  // Price + total value (when a unit cost was recorded — receive).
  const unitCost = Number(first.unitCost) || 0;
  const amount = unitCost > 0 ? formatMoney(unitCost * totalUnits, first.currencyCode) : null;
  if (unitCost > 0) {
    lines.push(`${formatMoney(unitCost, first.currencyCode)}/${unit || tr(t, "inventory:unit.default", "unit")} · ${amount} ${tr(t, "inventory:op.total", "total")}`);
  }

  // Location(s).
  if (op === INVENTORY_TXN_OP.TRANSFER || op === INVENTORY_TXN_OP.RETURN) {
    const src = rows.find((r) => Number(r.qtyDelta) < 0);
    const dest = rows.find((r) => Number(r.qtyDelta) > 0);
    const from = src ? balanceLocationLabel(src as unknown as InventoryBalance, opts.thisEventId, t) : "";
    const to = dest ? balanceLocationLabel(dest as unknown as InventoryBalance, opts.thisEventId, t) : "";
    if (from || to) {
      lines.push(`${from} → ${to}`);
    }
  } else {
    lines.push(balanceLocationLabel(first as unknown as InventoryBalance, opts.thisEventId, t));
  }

  // Lot / expiry.
  const expiry = formatExpiry(first.expiresAt);
  if (expiry) {
    lines.push(`${tr(t, "inventory:op.lot", "Lot")} ${expiry}`);
  }

  return { title: name, headline: qtyText, amount, lines, compact: `${opLabel} · ${name} · ${qtyText}` };
};

/** Discrete, column-ready fields for one completed operation (the Recent table). */
export interface OperationColumns {
  opLabel: string;
  itemName: string;
  /** On-hand change / counted quantity with unit, e.g. "64 cans" (or "+14 cans"). */
  qty: string;
  /** Pack math, e.g. "2 × 32" — null in units mode. */
  packNote: string | null;
  /** Per-unit cost, e.g. "$0.50" — null when no cost recorded. */
  unitCost: string | null;
  /** Total value moved, e.g. "$32.00" — null when no cost recorded. */
  total: string | null;
  /** Location label ("Home", "This event", "A → B" for a move). */
  location: string;
  vendor: string | null;
  expiry: string | null;
  /** Numeric units this op moved (for session totals). */
  qtyUnits: number;
  /** Numeric total value moved (for session totals) — null when no cost recorded. */
  valueNum: number | null;
  /** Op code (receive/consume/…) — for session totals (e.g. total received). */
  op: string;
  currencyCode: string | null;
}

/** Build the Recent-table columns for a completed op from its returned ledger rows. */
export const operationColumns = (
  opLabel: string,
  rows: InventoryTxn[],
  item?: Pick<InventoryItem, "name" | "unit"> | null,
  opts: { thisEventId?: string | null; t?: TFunction } = {},
): OperationColumns => {
  const first = rows[0] || ({} as InventoryTxn);
  const op = first.op || "";
  const unit = item?.unit;
  const t = opts.t;
  const name = item?.name || tr(t, "inventory:item.fallbackName", "Item");

  let totalUnits = 0;
  let qty = "";

  if (op === INVENTORY_TXN_OP.COUNT) {
    const counted = Number(first.countedQty) || 0;
    totalUnits = counted;
    qty = formatQtyWithUnit(counted, unit);
  } else if (op === INVENTORY_TXN_OP.ADJUST) {
    const delta = Number(first.qtyDelta) || 0;
    totalUnits = Math.abs(delta);
    qty = `${delta > 0 ? "+" : ""}${formatQtyWithUnit(delta, unit)}`;
  } else if (op === INVENTORY_TXN_OP.TRANSFER || op === INVENTORY_TXN_OP.RETURN) {
    totalUnits = rows.filter((r) => Number(r.qtyDelta) > 0).reduce((s, r) => s + (Number(r.qtyDelta) || 0), 0);
    qty = formatQtyWithUnit(totalUnits, unit);
  } else {
    totalUnits = rows.reduce((s, r) => s + Math.abs(Number(r.qtyDelta) || 0), 0);
    qty = formatQtyWithUnit(totalUnits, unit);
  }

  const packUnits = Number(first.packUnits) || 0;
  const packNote = packUnits > 1 && totalUnits > 0 ? `${Math.round((totalUnits / packUnits) * 100) / 100} × ${packUnits}` : null;

  const unitCostNum = Number(first.unitCost) || 0;
  const unitCost = unitCostNum > 0 ? formatMoney(unitCostNum, first.currencyCode) : null;
  const total = unitCostNum > 0 ? formatMoney(unitCostNum * totalUnits, first.currencyCode) : null;

  let location: string;

  if (op === INVENTORY_TXN_OP.TRANSFER || op === INVENTORY_TXN_OP.RETURN) {
    const src = rows.find((r) => Number(r.qtyDelta) < 0);
    const dest = rows.find((r) => Number(r.qtyDelta) > 0);
    const from = src ? balanceLocationLabel(src as unknown as InventoryBalance, opts.thisEventId, t) : "";
    const to = dest ? balanceLocationLabel(dest as unknown as InventoryBalance, opts.thisEventId, t) : "";
    location = from && to ? `${from} → ${to}` : from || to;
  } else {
    location = balanceLocationLabel(first as unknown as InventoryBalance, opts.thisEventId, t);
  }

  const vendor = first.vendor?.name || first.vendorLabel || null;
  const expiry = formatExpiry(first.expiresAt) || null;
  const valueNum = unitCostNum > 0 ? unitCostNum * totalUnits : null;

  return {
    opLabel,
    itemName: name,
    qty,
    packNote,
    unitCost,
    total,
    location,
    vendor,
    expiry,
    qtyUnits: totalUnits,
    valueNum,
    op,
    currencyCode: first.currencyCode ?? null,
  };
};
